import { useQuery } from '@tanstack/react-query'
import { SubgraphType } from 'config/subgraph'
import { useGetBlockMapQuery } from 'hooks/backend/blocks'
import { useConnection } from 'hooks/useConnection'
import { useSubgraphConfig } from 'hooks/useSubgraphConfig'
import { getLatestIndexedBlockNumber } from 'hooks/useSubgraphLatestIndexedBlock'
import { useGetLatestIndexedBlockQuery } from 'queries/analytics/v3/generated'
import { useMemo } from 'react'
import { computeDeltaTimestampsFromReference } from 'utils/queries'

type UseAnalyticsTimeWindowOptions = {
  enabled?: boolean
}

type UseAnalyticsTimeWindowResult = {
  timestamps?: [number, number, number]
  blocks?: Map<number, number>
  latestIndexedBlockNumber?: number
  indexedHeadTimestamp?: number
  isLoading: boolean
  isFetching: boolean
}

export const useAnalyticsTimeWindow = (options?: UseAnalyticsTimeWindowOptions): UseAnalyticsTimeWindowResult => {
  const enabled = options?.enabled ?? true
  const { chainId, wagmiPublicClient } = useConnection()
  const subgraphConfig = useSubgraphConfig(SubgraphType.V3)
  const endpoint = subgraphConfig.endpoint

  const latestIndexedBlockQuery = useGetLatestIndexedBlockQuery<number | undefined>({ endpoint }, undefined, {
    enabled,
    queryKey: ['subgraph-latest-indexed-block', endpoint],
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchInterval: false,
    select: getLatestIndexedBlockNumber,
  })

  const latestIndexedBlockNumber = latestIndexedBlockQuery.data

  const indexedHeadTimestampQuery = useQuery({
    queryKey: ['analytics-indexed-head-timestamp', endpoint, chainId, latestIndexedBlockNumber],
    enabled: enabled && latestIndexedBlockNumber !== undefined && Boolean(wagmiPublicClient),
    queryFn: async () => {
      if (!wagmiPublicClient || latestIndexedBlockNumber === undefined) {
        return undefined
      }

      const block = await wagmiPublicClient.getBlock({ blockNumber: BigInt(latestIndexedBlockNumber) })
      const blockTimestamp = Number(block.timestamp)
      return Number.isFinite(blockTimestamp) ? blockTimestamp : undefined
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchInterval: false,
  })

  const indexedHeadTimestamp = indexedHeadTimestampQuery.data

  const timestamps = useMemo(() => {
    if (indexedHeadTimestamp === undefined) return undefined
    return computeDeltaTimestampsFromReference(indexedHeadTimestamp)
  }, [indexedHeadTimestamp])

  const blocksQuery = useGetBlockMapQuery(
    {
      chainId,
      timestamps: timestamps ?? [],
    },
    {
      enabled: enabled && Boolean(timestamps),
    }
  )

  return {
    timestamps,
    blocks: blocksQuery.data,
    latestIndexedBlockNumber,
    indexedHeadTimestamp,
    isLoading: latestIndexedBlockQuery.isLoading || indexedHeadTimestampQuery.isLoading || blocksQuery.isLoading,
    isFetching: latestIndexedBlockQuery.isFetching || indexedHeadTimestampQuery.isFetching || blocksQuery.isFetching,
  }
}
